import { useState, useMemo, useCallback, useContext } from "react";
import { Product } from "../../../../interfaces/sales";
import { partialCancelSaleService } from "../../../../services/sales";
import { getServiceError } from "../../../../utils/getServiceError";
import { ErrorModalContext } from "../../../../components/AlertsModal/AlertsModal";
import {
  PartialCancelStep,
  SalePayment,
} from "../interfaces/partialCancellation";

interface UsePartialCancellationProps {
  products: Product[];
  payments: SalePayment[];
  saleUuid: string;
  saleCode: string;
  onSuccess: () => void;
  closeModal: () => void;
}

// Payments with status Refunded (4) or RefundPending (3) are not refundable
const isRefundablePayment = (status: number | string): boolean => {
  const s = Number(status);
  return s !== 3 && s !== 4;
};

export const usePartialCancellation = ({
  products,
  payments: allPayments,
  saleUuid,
  saleCode,
  onSuccess,
  closeModal,
}: UsePartialCancellationProps) => {
  const { showAlert } = useContext(ErrorModalContext);
  const payments = useMemo(
    () => allPayments.filter((p) => isRefundablePayment(p.status)),
    [allPayments],
  );
  const [step, setStep] = useState<PartialCancelStep>("products");
  const [selectedProducts, setSelectedProducts] = useState<
    Record<string, number>
  >({});
  const [paymentRefunds, setPaymentRefunds] = useState<
    Record<string, number>
  >({});
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneMatch, setPhoneMatch] = useState(false);
  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const refundTotal = useMemo(() => {
    return Object.entries(selectedProducts).reduce(
      (total, [uuid, cancelQty]) => {
        if (cancelQty <= 0) return total;
        const product = products.find((p) => p.uuid === uuid);
        if (!product) return total;
        return total + product.price * cancelQty;
      },
      0,
    );
  }, [selectedProducts, products]);

  const isAllSelected = useMemo(() => {
    const activeProducts = products.filter((p) => p.quantity > 0);
    if (activeProducts.length === 0) return false;
    return activeProducts.every(
      (p) => (selectedProducts[p.uuid] || 0) >= p.quantity,
    );
  }, [selectedProducts, products]);

  const hasSelectedProducts = useMemo(
    () => Object.values(selectedProducts).some((qty) => qty > 0),
    [selectedProducts],
  );

  const hasCardRefund = useMemo(() => {
    if (payments.length <= 1) {
      // Single payment: check if it's a card payment and products are selected
      const payment = payments[0];
      return hasSelectedProducts && !!payment?.card_transaction;
    }
    // Multi-payment: check if any card payment has a refund amount > 0
    return Object.entries(paymentRefunds).some(([uuid, amount]) => {
      if (amount <= 0) return false;
      const payment = payments.find((p) => p.uuid === uuid);
      return payment && !!payment.card_transaction;
    });
  }, [payments, paymentRefunds, hasSelectedProducts]);

  const isPhoneNumberValid = useMemo(
    () => phoneNumber.length >= 10 && phoneMatch,
    [phoneNumber, phoneMatch],
  );

  const isPaymentDistributionValid = useMemo(() => {
    if (payments.length <= 1) return true;
    const totalDistributed = Math.round(
      Object.values(paymentRefunds).reduce((sum, amount) => sum + (amount || 0), 0) * 100
    ) / 100;
    return Math.abs(totalDistributed - refundTotal) < 0.01;
  }, [paymentRefunds, refundTotal, payments]);

  const handleProductSelection = useCallback(
    (uuid: string, quantity: number) => {
      const product = products.find((p) => p.uuid === uuid);
      if (!product) return;

      const clampedQty = Math.max(0, Math.min(quantity, product.quantity));

      setSelectedProducts((prev) => {
        const next = { ...prev };

        // Block manual changes to addons — they are auto-managed by parent
        if (product.uuid_parent) {
          return prev;
        }

        if (clampedQty === 0) {
          delete next[uuid];
        } else {
          next[uuid] = clampedQty;
        }

        // Auto-adjust addon quantities proportionally to parent cancel qty
        const addons = products.filter(
          (p) => p.uuid_parent === product.uuid,
        );
        addons.forEach((addon) => {
          const addonPerUnit = addon.quantity / product.quantity;
          const addonCancelQty = Math.floor(clampedQty * addonPerUnit);
          if (addonCancelQty === 0) {
            delete next[addon.uuid];
          } else {
            next[addon.uuid] = addonCancelQty;
          }
        });

        return next;
      });
    },
    [products],
  );

  const handlePaymentRefund = useCallback(
    (uuid: string, amount: number) => {
      setPaymentRefunds((prev) => ({
        ...prev,
        [uuid]: amount,
      }));
    },
    [],
  );

  const goToNextStep = useCallback(() => {
    if (step === "products") {
      if (!hasSelectedProducts) {
        return;
      }
      if (payments.length <= 1) {
        // Single payment: skip payment step, check if phone step needed
        if (hasCardRefund) {
          setStep("phone");
        } else {
          setStep("comment");
        }
      } else {
        const initialRefunds: Record<string, number> = {};
        payments.forEach((p) => {
          initialRefunds[p.uuid] = paymentRefunds[p.uuid] || 0;
        });
        setPaymentRefunds(initialRefunds);
        setStep("payments");
      }
    } else if (step === "payments") {
      if (!isPaymentDistributionValid) {
        return;
      }
      if (hasCardRefund) {
        setStep("phone");
      } else {
        setStep("comment");
      }
    } else if (step === "phone") {
      if (!isPhoneNumberValid) {
        return;
      }
      setStep("comment");
    } else if (step === "comment") {
      setStep("confirm");
    }
  }, [
    step,
    hasSelectedProducts,
    payments,
    paymentRefunds,
    isPaymentDistributionValid,
    hasCardRefund,
    isPhoneNumberValid,
  ]);

  const goToPreviousStep = useCallback(() => {
    if (step === "confirm") {
      setStep("comment");
    } else if (step === "comment") {
      if (hasCardRefund) {
        setStep("phone");
      } else if (payments.length > 1) {
        setStep("payments");
      } else {
        setStep("products");
      }
    } else if (step === "phone") {
      if (payments.length <= 1) {
        setStep("products");
      } else {
        setStep("payments");
      }
    } else if (step === "payments") {
      setStep("products");
    }
  }, [step, payments, hasCardRefund]);

  const submit = useCallback(async () => {
    try {
      setIsLoading(true);

      const productsToCancel = Object.entries(selectedProducts)
        .filter(([, qty]) => qty > 0)
        .map(([uuid_sale_product, quantity]) => ({ uuid_sale_product, quantity }));

      const paymentsDistribution =
        payments.length > 1
          ? Object.entries(paymentRefunds)
              .filter(([, amount]) => amount > 0)
              .map(([uuid, refund_amount]) => ({ uuid, refund_amount }))
          : undefined;

      await partialCancelSaleService(saleUuid, {
        products: productsToCancel,
        payments: paymentsDistribution,
        phone_number: hasCardRefund ? phoneNumber : undefined,
        comment,
      });

      setIsLoading(false);
      closeModal();
      showAlert(
        `La venta ${saleCode} ha sido parcialmente cancelada.`,
        "success",
        "Cancelación parcial exitosa",
      );
      onSuccess();
    } catch (error) {
      setIsLoading(false);
      const msg = getServiceError(error);
      showAlert(msg, "error");
    }
  }, [
    selectedProducts,
    paymentRefunds,
    payments,
    saleUuid,
    saleCode,
    closeModal,
    showAlert,
    onSuccess,
    hasCardRefund,
    phoneNumber,
    comment,
  ]);

  const reset = useCallback(() => {
    setStep("products");
    setSelectedProducts({});
    setPaymentRefunds({});
    setPhoneNumber("");
    setComment("");
    setIsLoading(false);
  }, []);

  return {
    step,
    selectedProducts,
    payments,
    paymentRefunds,
    phoneNumber,
    comment,
    refundTotal,
    isLoading,
    isAllSelected,
    hasSelectedProducts,
    hasCardRefund,
    isPhoneNumberValid,
    isPaymentDistributionValid,
    handleProductSelection,
    handlePaymentRefund,
    setPhoneNumber,
    setPhoneMatch,
    setComment,
    goToNextStep,
    goToPreviousStep,
    submit,
    reset,
  };
};
